import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { COST_CATEGORIES, COST_UNITS } from '@/lib/cost-utils';

interface Cost {
  id: string;
  kodKos: string;
  projek: string;
  kategori: string;
  penerangan: string;
  kuantiti: number;
  unit: string;
  hargaPerUnit: number;
  jumlah: number;
  tarikh: string;
  catatan?: string;
}

interface CostFormProps {
  cost?: Cost;
  kodKos: string;
  projects?: any[];
  onSubmit: (data: Cost) => void;
  onCancel: () => void;
}

export const CostForm = ({ cost, kodKos, projects = [], onSubmit, onCancel }: CostFormProps) => {
  const [formData, setFormData] = useState<Partial<Cost>>(
    cost || {
      id: Date.now().toString(),
      kodKos: kodKos,
      projek: '',
      kategori: 'Bahan',
      penerangan: '',
      kuantiti: 1,
      unit: 'tin',
      hargaPerUnit: 0,
      jumlah: 0,
      tarikh: new Date().toISOString().split('T')[0],
      catatan: '',
    }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    let newValue: any = value;
    if (name === 'kuantiti' || name === 'hargaPerUnit') {
      newValue = parseFloat(value) || 0;
    }
    
    const updatedData = { ...formData, [name]: newValue };
    
    // Auto-calculate jumlah (kuantiti x hargaPerUnit)
    if (name === 'kuantiti' || name === 'hargaPerUnit') {
      updatedData.jumlah = (updatedData.kuantiti || 0) * (updatedData.hargaPerUnit || 0);
    }
    
    setFormData(updatedData);
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!formData.projek?.trim()) newErrors.projek = 'Pilih projek diperlukan';
    if (!formData.penerangan?.trim()) newErrors.penerangan = 'Penerangan item diperlukan';
    if (!formData.kuantiti || formData.kuantiti <= 0) newErrors.kuantiti = 'Kuantiti harus lebih dari 0';
    if (!formData.hargaPerUnit || formData.hargaPerUnit < 0) newErrors.hargaPerUnit = 'Harga tidak boleh negatif';
    if (!formData.tarikh?.trim()) newErrors.tarikh = 'Tarikh diperlukan';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData as Cost);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex justify-between items-center sticky top-0 bg-white border-b">
          <CardTitle>Tambah Kos Baru</CardTitle>
          <button onClick={onCancel} className="p-2 hover:bg-slate-100 rounded">
            <X size={20} />
          </button>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Kod Kos (Auto) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Kod Kos</label>
              <Input
                type="text"
                value={formData.kodKos || ''}
                disabled
                className="bg-slate-100 text-slate-500"
              />
              <p className="text-xs text-slate-500">Kod akan di-jana secara automatik</p>
            </div>

            {/* Pilih Projek */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Projek *</label>
              <select
                name="projek"
                value={formData.projek || ''}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  errors.projek ? 'border-red-500' : 'border-slate-300'
                }`}
              >
                <option value="">Pilih projek</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.namaProjek}>
                    {p.namaProjek} ({p.kodProjek})
                  </option>
                ))}
              </select>
              {errors.projek && <p className="text-sm text-red-600">{errors.projek}</p>}
            </div>

            {/* Kategori */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Kategori</label>
              <select
                name="kategori"
                value={formData.kategori || 'Bahan'}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {COST_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Penerangan Item Kos */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Penerangan Item Kos *</label>
              <Input
                type="text"
                name="penerangan"
                value={formData.penerangan || ''}
                onChange={handleChange}
                placeholder="Cth. Cat Nippon Q-Shield 5L"
                className={errors.penerangan ? 'border-red-500' : ''}
              />
              {errors.penerangan && <p className="text-sm text-red-600">{errors.penerangan}</p>}
            </div>

            {/* Kuantiti, Unit, Harga */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Kuantiti *</label>
                <Input
                  type="number"
                  name="kuantiti"
                  value={formData.kuantiti || ''}
                  onChange={handleChange}
                  placeholder="1"
                  min="0"
                  step="0.01"
                  className={errors.kuantiti ? 'border-red-500' : ''}
                />
                {errors.kuantiti && <p className="text-sm text-red-600">{errors.kuantiti}</p>}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Unit</label>
                <select
                  name="unit"
                  value={formData.unit || 'tin'}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {COST_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Harga/Unit (RM) *</label>
                <Input
                  type="number"
                  name="hargaPerUnit"
                  value={formData.hargaPerUnit || ''}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className={errors.hargaPerUnit ? 'border-red-500' : ''}
                />
                {errors.hargaPerUnit && <p className="text-sm text-red-600">{errors.hargaPerUnit}</p>}
              </div>
            </div>

            {/* Jumlah (Auto) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Jumlah (RM) — Auto-kira</label>
              <Input
                type="number"
                value={formData.jumlah || 0}
                disabled
                placeholder="0.00"
                className="bg-slate-100 text-slate-500"
              />
              <p className="text-xs text-slate-500">Dikira otomatis: Kuantiti × Harga/Unit</p>
            </div>

            {/* Tarikh */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Tarikh *</label>
              <Input
                type="date"
                name="tarikh"
                value={formData.tarikh || ''}
                onChange={handleChange}
                className={errors.tarikh ? 'border-red-500' : ''}
              />
              {errors.tarikh && <p className="text-sm text-red-600">{errors.tarikh}</p>}
            </div>

            {/* Catatan */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Catatan</label>
              <textarea
                name="catatan"
                value={formData.catatan || ''}
                onChange={handleChange}
                placeholder="Catatan tambahan (pilihan)"
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t">
              <Button type="submit" className="bg-red-600 hover:bg-red-700 flex-1">
                Simpan
              </Button>
              <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                Batal
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
